// Shared component (TS-2.5.03). BRD section 7.7 / decisions.md D-011: a messy date field accepts
// a real date, a numeric string, month-name text, OR a placeholder token ("TBD", "ongoing",
// "Formular", ...) - and whatever is typed is stored exactly as typed, never silently rewritten
// (EchoBackDate, PortfolioDataManager.WriteHelpers.cs). This control is therefore always a plain
// text field at heart (so any of those shapes can be typed into it) with two assists layered on,
// neither of which narrows what can be submitted:
//   - a native <input type="date"> picker that, when used, writes its ISO value into the text
//     field rather than replacing the control, so picking a date and typing free text share one
//     value and one field name on the wire;
//   - a SuggestionInput (owner ruling 2026-10-03, step two) offering the placeholder vocabulary
//     (ReferenceListCodes.DatePlaceholder, "date.placeholder"), fetched by the caller via
//     useReferenceList and passed in - never hardcoded here, since only the server's reference
//     data knows which literal tokens DateParsingOptions.PlaceholderTokens currently recognizes.
//     This replaced a plain `<input list>` + `<datalist>` pair for the same reason every other
//     OPEN field did (see that component's header): a datalist filters by the field's current
//     text, so once a placeholder was already typed the rest of the vocabulary became invisible.
//     Picking a suggestion writes its TOKEN (SuggestionInputOption.value) - the literal text this
//     field stores - never the friendlier `label` passed in alongside it for display only.
// `interpretation` renders the server's own echo-back (ValidationIssue for
// PortfolioIssueCodes.DateEchoBack / DataQualityCodes.DateUnparseable /
// DataQualityCodes.PlaceholderStillPresent) once a save response has carried one for this field -
// never a client-side guess at how the date was read.
import { SuggestionInput } from "./SuggestionInput";
import type { SuggestionInputOption } from "./SuggestionInput";

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface DateFieldPlaceholderOption {
  readonly token: string;
  readonly label: string;
}

export interface DateFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholderOptions?: readonly DateFieldPlaceholderOption[];
  readonly interpretation?: string | null;
  readonly description?: string;
  readonly error?: string | null;
}

export function DateField({
  id,
  label,
  value,
  onChange,
  placeholderOptions = [],
  interpretation,
  description,
  error,
}: DateFieldProps) {
  const pickerValue = ISO_DATE_PATTERN.test(value) ? value : "";
  const suggestions: readonly SuggestionInputOption[] = placeholderOptions.map((option) => ({
    value: option.token,
    label: option.label,
  }));

  return (
    <div className="eg-date-field">
      <div className="eg-date-field__row">
        <SuggestionInput
          id={id}
          value={value}
          onChange={onChange}
          suggestions={suggestions}
          description={description}
          error={error}
        />
        <input
          type="date"
          aria-label={`Pick a date for ${label}`}
          value={pickerValue}
          onChange={(event) => {
            if (event.target.value) onChange(event.target.value);
          }}
        />
      </div>
      {interpretation && <p className="eg-date-field__interpretation">{interpretation}</p>}
    </div>
  );
}
